import { store } from '../store';

/**
 * Utility functions for user-related operations
 */

/**
 * Gets the current logged-in username from Redux store
 * @returns The current username (email) or empty string if not available
 */
export const getCurrentUsername = (): string => {
  try {
    // Get user from Redux store
    const state = store.getState();
    const user = state.user;

    // Priority 1: username field (most preferred)
    if (user?.username) {
      return user.username;
    }

    // Priority 2: email field
    if (user?.email) {
      return user.email;
    }

    // Priority 3: Construct from first_name and last_name
    if (user?.first_name || user?.last_name) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      if (fullName) {
        return fullName;
      }
    }

    // Priority 4: user ID as fallback
    if (user?.id) {
      return user.id;
    }

    return '';
  } catch (error) {
    console.warn('Error getting current username:', error);
    return '';
  }
};

/**
 * Creates a label with username prefix for API payloads
 * @param baseLabel - The base label value (e.g., solution ID, draft ID, file name)
 * @param username - Optional username (will be fetched from Redux if not provided)
 * @returns Label with username prefix in format "username:baseLabel"
 *
 * @example
 * // Returns "user@example.com:solution-123"
 * createLabelWithUsername('solution-123')
 *
 * @example
 * // Returns "custom-user:draft-456"
 * createLabelWithUsername('draft-456', 'custom-user')
 */
export const createLabelWithUsername = (baseLabel: string, username?: string): string => {
  const currentUsername = username || getCurrentUsername();

  // If no username or no baseLabel, return baseLabel as-is
  if (!currentUsername || !baseLabel) {
    return baseLabel || '';
  }

  return `${currentUsername}:${baseLabel}`;
};
